import { Link } from "@inertiajs/react";
import { useState } from "react";
import AppLayout from "../../layouts/AppLayout";
import { BarChart2, TrendingUp, Users, Shield, CheckCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import OutboundAnalytics from "./OutboundAnalytics";

const STAGE_LABELS: Record<string, string> = {
  new_lead:        "New Lead",
  intake_scheduled: "Intake",
  resume_editing:  "Resume Edit",
  package_pending: "Pkg Pending",
  submitted:       "Submitted",
  screener:        "Screener",
  full_round:      "Full Round",
  offer_placed:    "Offer",
};

const SOURCE_LABELS: Record<string, string> = {
  leopard:            "Leopard",
  linkedin:           "LinkedIn",
  inbound:            "Inbound",
  referral:           "Referral",
  cold_email_response: "Cold Email",
};

interface StageCount   { stage: string; count: number }
interface StageVelocity { stage: string; avg_days: number | null }
interface SourceItem   { source: string; count: number }
interface WeekItem     { week: string; count: number }

interface FunnelStep { stage: string; label: string; count: number; pct: number }
interface InterviewCandidate {
  id: number; name: string; pipeline_stage: string;
  days_in_stage: number; firm_name: string | null; last_activity: string;
}
// OutboundStats and SkmPerformance shapes are defined in OutboundAnalytics.tsx
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type OutboundStats   = any;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SkmPerformance  = any;

interface DncStats {
  total_checks: number;
  blocked_count: number;
  clear_count: number;
  sheet_checks: number;
  exchange_checks: number;
  block_rate: number;
}

interface Totals {
  total_candidates: number;
  active_pipeline: number;
  outreach_eligible: number;
  placements_ytd: number;
  packages_executed: number;
}

interface Props {
  stage_counts:         StageCount[];
  stage_velocity:       StageVelocity[];
  source_breakdown:     SourceItem[];
  dnc_stats:            DncStats;
  weekly_activity:      WeekItem[];
  totals:               Totals;
  conversion_funnel:    FunnelStep[];
  interview_candidates: InterviewCandidate[];
  outbound_stats:       OutboundStats;
  skm_performance:      SkmPerformance;
}

type AnalyticsTab = "pipeline" | "outbound";

function BarRow({ label, value, max, color = "bg-green-500" }: { label: string; value: number; max: number; color?: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="w-24 text-right text-xs text-gray-500 shrink-0">{label}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-2">
        <div className={`${color} h-2 rounded-full transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-8 text-right text-xs font-medium text-gray-700">{value}</span>
    </div>
  );
}

export default function AnalyticsIndex({ stage_counts, stage_velocity, source_breakdown, dnc_stats, weekly_activity, totals, conversion_funnel, interview_candidates, outbound_stats, skm_performance }: Props) {
  const [activeTab, setActiveTab] = useState<AnalyticsTab>("pipeline");
  const maxStage   = Math.max(...stage_counts.map((s) => s.count), 1);
  const maxSource  = Math.max(...source_breakdown.map((s) => s.count), 1);
  const maxWeek    = Math.max(...weekly_activity.map((w) => w.count), 1);
  const velocityStages = stage_velocity.filter((s) => s.avg_days !== null);

  return (
    <AppLayout title="Analytics">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-sm text-gray-500 mt-0.5">Pipeline health and activity overview</p>
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm mb-6 w-fit">
        {(["pipeline", "outbound"] as AnalyticsTab[]).map((tab) => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 font-medium capitalize transition-colors ${activeTab === tab ? "bg-gray-900 text-white" : "text-gray-600 hover:bg-gray-50"}`}>
            {tab === "pipeline" ? "Pipeline" : "Outbound & SKM"}
          </button>
        ))}
      </div>

      {activeTab === "outbound" && (
        <OutboundAnalytics outbound_stats={outbound_stats} skm_performance={skm_performance} />
      )}

      {activeTab === "pipeline" && (<>
      {/* Top stat cards */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-6">
        {[
          { label: "Total Candidates",   value: totals.total_candidates,  icon: Users,       color: "text-gray-600" },
          { label: "Active Pipeline",    value: totals.active_pipeline,   icon: TrendingUp,  color: "text-blue-600" },
          { label: "Outreach Eligible",  value: totals.outreach_eligible, icon: CheckCircle, color: "text-green-600" },
          { label: "Placements YTD",     value: totals.placements_ytd,    icon: BarChart2,   color: "text-purple-600" },
          { label: "Packages Executed",  value: totals.packages_executed, icon: Shield,      color: "text-amber-600" },
        ].map(({ label, value, icon: Icon, color }) => (
          <Card key={label}>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-gray-500">{label}</span>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <p className="text-2xl font-bold text-gray-900">{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Pipeline stage distribution */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><BarChart2 className="w-4 h-4 text-gray-400" /> Pipeline Distribution</CardTitle></CardHeader>
          <CardContent className="space-y-2.5">
            {stage_counts.map((s) => (
              <BarRow key={s.stage} label={STAGE_LABELS[s.stage] ?? s.stage} value={s.count} max={maxStage} />
            ))}
          </CardContent>
        </Card>

        {/* Weekly activity */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><TrendingUp className="w-4 h-4 text-gray-400" /> Weekly New Candidates (8 weeks)</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-end gap-1.5 h-32">
              {weekly_activity.map((w) => {
                const pct = maxWeek > 0 ? (w.count / maxWeek) * 100 : 0;
                return (
                  <div key={w.week} className="flex-1 flex flex-col items-center gap-1">
                    <span className="text-xs text-gray-700 font-medium">{w.count > 0 ? w.count : ""}</span>
                    <div className="w-full bg-green-500 rounded-t" style={{ height: `${Math.max(pct, w.count > 0 ? 4 : 0)}%` }} />
                    <span className="text-[10px] text-gray-400 rotate-[-30deg] origin-top-left translate-x-1">{w.week.split(" ")[1]}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stage velocity */}
        <Card>
          <CardHeader><CardTitle className="text-base">Avg Days per Stage</CardTitle></CardHeader>
          <CardContent className="space-y-2.5">
            {velocityStages.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">Not enough data yet</p>
            ) : (
              velocityStages.map((s) => {
                const maxDays = Math.max(...velocityStages.map((x) => x.avg_days ?? 0), 1);
                return (
                  <BarRow
                    key={s.stage}
                    label={STAGE_LABELS[s.stage] ?? s.stage}
                    value={s.avg_days ?? 0}
                    max={maxDays}
                    color={s.avg_days && s.avg_days > 14 ? "bg-red-400" : "bg-blue-400"}
                  />
                );
              })
            )}
          </CardContent>
        </Card>

        {/* Source breakdown */}
        <Card>
          <CardHeader><CardTitle className="text-base">Candidate Sources</CardTitle></CardHeader>
          <CardContent className="space-y-2.5">
            {source_breakdown.map((s) => (
              <BarRow key={s.source} label={SOURCE_LABELS[s.source] ?? s.source} value={s.count} max={maxSource} color="bg-purple-400" />
            ))}
          </CardContent>
        </Card>

        {/* DNC stats */}
        <Card>
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4 text-gray-400" /> DNC Checks (30 days)</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: "Total Checks",    value: dnc_stats.total_checks },
                { label: "Block Rate",      value: `${dnc_stats.block_rate}%` },
                { label: "Sheet Checks",    value: dnc_stats.sheet_checks },
                { label: "Exchange Checks", value: dnc_stats.exchange_checks },
                { label: "Cleared",         value: dnc_stats.clear_count },
                { label: "Blocked",         value: dnc_stats.blocked_count },
              ].map(({ label, value }) => (
                <div key={label} className="bg-gray-50 rounded-lg p-2.5">
                  <p className="text-xs text-gray-500">{label}</p>
                  <p className="text-lg font-semibold text-gray-900 mt-0.5">{value}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      {/* Conversion funnel */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Conversion Funnel</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {conversion_funnel.map((step, i) => (
              <div key={step.stage} className="flex items-center gap-3 text-sm">
                <span className="w-20 text-right text-xs text-gray-500 shrink-0">{step.label}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full ${i === 0 ? "bg-gray-400" : i < 4 ? "bg-blue-400" : i === 4 ? "bg-purple-500" : "bg-green-600"}`}
                    style={{ width: `${step.pct}%` }}
                  />
                </div>
                <span className="w-10 text-right text-xs font-medium text-gray-700">{step.count}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Interview status overview */}
        <Card>
          <CardHeader><CardTitle className="text-base">In Interview Now</CardTitle></CardHeader>
          <CardContent>
            {interview_candidates.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-6">No candidates currently in interview stages</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                  <thead>
                    <tr className="text-gray-400 uppercase text-[10px]">
                      <th className="text-left pb-2 font-medium">Candidate</th>
                      <th className="text-left pb-2 font-medium">Stage</th>
                      <th className="text-left pb-2 font-medium">Firm</th>
                      <th className="text-right pb-2 font-medium">Days</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {interview_candidates.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="py-1.5">
                          <Link href={`/candidates/${c.id}`} className="text-gray-900 hover:text-green-700 font-medium">{c.name}</Link>
                        </td>
                        <td className="py-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${c.pipeline_stage === "full_round" ? "bg-purple-100 text-purple-700" : "bg-amber-100 text-amber-700"}`}>
                            {c.pipeline_stage === "full_round" ? "Full Round" : "Screener"}
                          </span>
                        </td>
                        <td className="py-1.5 text-gray-500">{c.firm_name ?? "—"}</td>
                        <td className={`py-1.5 text-right font-medium ${c.days_in_stage > 14 ? "text-red-600" : "text-gray-700"}`}>
                          {c.days_in_stage}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      </>)}
    </AppLayout>
  );
}
